import { useState } from "react";
import { useShareGate } from "@/hooks/useShareGate";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { generateCaptions } from "@/lib/captions";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TOOL_INFO_CONTENT = {
  what: `Generates draft social media captions in four tones (fun, professional, bold, Gen-Z) from a topic and an optional specific detail, as a starting point to edit into your own voice.`,
  how: `Describe your post or topic, generate a caption draft, and edit it to match your own tone before posting.`,
  tip: `Treat generated captions as a first draft, not a final post — the captions that perform best usually have a specific, personal detail that a generic starting point won't include on its own.`,
};

export default function CaptionGenerator() {
  const { gateShare } = useShareGate();
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState<"instagram" | "tiktok" | "x" | "linkedin">("instagram");
  const [tone, setTone] = useState<"fun" | "professional" | "bold" | "genz">("fun");
  const [detail, setDetail] = useState("");
  const [cta, setCta] = useState(false);
  const [captions, setCaptions] = useState<string[]>([]);

  const { logUsageNow } = useLogUsageOnAction();

  const generate = async () => {
    if (!topic.trim()) return;
    const allowed = await logUsageNow();
    if (!allowed) return;
    setCaptions(generateCaptions(topic, platform, tone, { detail, cta }));
  };


  return (
    <ToolContainer>
      <div className="space-y-4">
        <div><Label>Topic / Description</Label><Input aria-label="Topic / Description" value={topic} onChange={e => setTopic(e.target.value)} placeholder="What's your post about?" /></div>
        <div><Label>A specific detail (optional)</Label><Input aria-label="A specific detail (optional)" value={detail} onChange={e => setDetail(e.target.value)} placeholder="e.g. 20% off until Friday, or what surprised you" /></div>
        <div className="flex gap-3">
          <div className="flex-1">
            <Label>Platform</Label>
            <Select value={platform} onValueChange={(v) => setPlatform(v as typeof platform)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="tiktok">TikTok</SelectItem>
                <SelectItem value="x">X (Twitter)</SelectItem>
                <SelectItem value="linkedin">LinkedIn</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Label>Tone</Label>
            <Select value={tone} onValueChange={(v) => setTone(v as typeof tone)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fun">Fun</SelectItem>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="bold">Bold</SelectItem>
                <SelectItem value="genz">Gen-Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={cta} onChange={e => setCta(e.target.checked)} /> Add a call to action</label>
        <Button onClick={generate} disabled={!topic.trim()} className="gap-2">
          <Sparkles className="h-4 w-4" /> Generate Captions
        </Button>
        {captions.length > 0 && (
          <div className="space-y-3">
            {captions.map((c, i) => (
              <div key={i} className="p-4 rounded-lg border bg-card text-sm whitespace-pre-wrap flex items-start justify-between gap-3">
                <span>{c}<span className="block text-xs text-muted-foreground mt-1">{c.length} characters{platform === "x" && c.length > 280 ? " - over the 280 limit, shorten the topic" : ""}</span></span>
                <Button size="sm" variant="outline" onClick={() => { void gateShare(async () => { try { await navigator.clipboard.writeText(c); toast.success("Copied!"); return true; } catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; } }); }}>Copy</Button>
              </div>
            ))}
          </div>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
